import {
  Button,
  Icon,
  MoreMenuContent,
  Popover,
  PopoverContent,
  PopoverTrigger,
  SplitButton,
  SplitButtonSkeleton
} from '@sollapay/ui/components';
import {memo, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {getRosterPeople, TeamAvatarStack} from '@/features/trust-accounts/members';

import {SharePanelContent} from './SharePanelContent';
import {
  useShareLink,
  useSharePanelData,
  useSharePanelParam,
  useTrustAccountGuestsQuery
} from '../hooks';

import type {SharePanelContentProps} from './SharePanelContent';
import type {ShareView} from '../utils';
import type {TeamPerson} from '@/features/trust-accounts/members';
import type {TrustAccountGuestDTO, TrustAccountMembersDTO} from '@sollapay/types';
import type {MoreMenuItem} from '@sollapay/ui/components';
import type {TFunction} from 'i18next';
import type {FC} from 'react';

const NO_GUESTS: TrustAccountGuestDTO[] = [];

export interface TrustAccountShareButtonProps {
  trustAccountId: string;
  accountName: string;
  roster: TrustAccountMembersDTO;
  /** The account's own actions (e.g. edit), listed above the sharing ones in the menu. */
  accountMenuItems?: MoreMenuItem[];
}

/**
 * The header's split Share button, showing everyone with access: opens the share panel, and its menu
 * holds the account actions. `?share=people` or `?share=log` in the URL opens the panel on that view.
 */
export const TrustAccountShareButton: FC<TrustAccountShareButtonProps> = ({
  trustAccountId,
  accountName,
  roster,
  accountMenuItems = []
}) => {
  const {t} = useTranslation('trustAccounts');
  const anchorRef = useRef<HTMLDivElement>(null);
  const {panelView, setPanelView} = useSharePanelParam();
  const {data, isPending} = useTrustAccountGuestsQuery(trustAccountId);
  const {copyLink} = useShareLink(trustAccountId);
  const guests = data?.guests ?? NO_GUESTS;
  const isOpen = panelView !== null;

  if (isPending) return <SplitButtonSkeleton />;

  return (
    <SplitButton ref={anchorRef} aria-label={t('share.trigger.label')}>
      <Popover open={isOpen} onOpenChange={open => setPanelView(open ? 'people' : null)}>
        <SharePanelTrigger roster={roster} guests={guests} />
        <PopoverContent
          anchor={anchorRef}
          align="end"
          sideOffset={8}
          aria-label={t('share.panel.title', {name: accountName})}
          className="max-h-(--available-height) w-140 max-w-(--available-width) gap-0 overflow-y-auto rounded-xxl p-0"
        >
          <SharePanel
            trustAccountId={trustAccountId}
            roster={roster}
            guests={guests}
            accountName={accountName}
            view={panelView ?? 'people'}
            onViewChange={setPanelView}
            onClose={() => setPanelView(null)}
          />
        </PopoverContent>
      </Popover>
      <ShareAccountMenu
        accountMenuItems={accountMenuItems}
        onOpen={setPanelView}
        onCopyLink={() => void copyLink({showToast: true})}
      />
    </SplitButton>
  );
};

type SharePanelProps = Pick<
  SharePanelContentProps,
  'accountName' | 'view' | 'onViewChange' | 'onClose'
> & {
  trustAccountId: string;
  roster: TrustAccountMembersDTO;
  guests: TrustAccountGuestDTO[];
};

/** The open panel owns its data and actions, so the header button doesn't re-render as they change. */
const SharePanel: FC<SharePanelProps> = ({trustAccountId, roster, guests, ...panel}) => {
  const data = useSharePanelData({trustAccountId, roster, guests, view: panel.view});

  return <SharePanelContent {...panel} {...data} />;
};

interface SharePanelTriggerProps {
  roster: TrustAccountMembersDTO;
  guests: TrustAccountGuestDTO[];
}

const SharePanelTrigger: FC<SharePanelTriggerProps> = memo(({roster, guests}) => {
  const {t} = useTranslation('trustAccounts');
  const people = useMemo(() => getPeopleWithAccess(roster, guests), [roster, guests]);

  return (
    <PopoverTrigger
      render={
        <Button
          variant="selected"
          title={getAccessSummary(roster, guests, t)}
          icon={<Icon name="users" className="size-4.5" aria-hidden="true" />}
        />
      }
    >
      {t('share.trigger.label')}
      {people.length > 0 && (
        <span aria-hidden="true" className="ms-1">
          <TeamAvatarStack members={people} size="sm" />
        </span>
      )}
    </PopoverTrigger>
  );
});
SharePanelTrigger.displayName = 'SharePanelTrigger';

interface ShareAccountMenuProps {
  accountMenuItems: MoreMenuItem[];
  onOpen: (view: ShareView) => void;
  onCopyLink: () => void;
}

const ShareAccountMenu: FC<ShareAccountMenuProps> = ({accountMenuItems, onOpen, onCopyLink}) => {
  const {t} = useTranslation('trustAccounts');
  const [isOpen, setIsOpen] = useState(false);
  const items = [...accountMenuItems, ...getSharingMenuItems({t, onOpen, onCopyLink})];

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger
        render={
          <Button
            variant="selected"
            size="icon"
            aria-label={t('tableActions.openMenu')}
            icon={<Icon name="dots-vertical" className="size-4.5" aria-hidden="true" />}
          />
        }
      />
      <MoreMenuContent size="lg" items={items} onSelect={() => setIsOpen(false)} />
    </Popover>
  );
};

/** The menu's sharing group, below the account's own actions. */
function getSharingMenuItems(params: {
  t: TFunction;
  onOpen: (view: ShareView) => void;
  onCopyLink: () => void;
}): MoreMenuItem[] {
  const {t, onOpen, onCopyLink} = params;
  return [
    {
      icon: 'users',
      label: t('share.menu.manage'),
      heading: t('share.trigger.label'),
      separator: true,
      onClick: () => onOpen('people')
    },
    {icon: 'link', label: t('share.footer.copy'), onClick: onCopyLink},
    {icon: 'clock', label: t('share.views.log'), onClick: () => onOpen('log')}
  ];
}

/** Everyone the trustee shared the account with; the trustee isn't shown on their own button. */
function getPeopleWithAccess(
  roster: TrustAccountMembersDTO,
  guests: TrustAccountGuestDTO[]
): TeamPerson[] {
  return [
    ...roster.members,
    ...guests.map(({userId, name, email}) => ({userId, email, name: name ?? undefined}))
  ];
}

/** "3 from the firm · 2 guests": the button's tooltip, counting the trustee with the firm. */
function getAccessSummary(
  roster: TrustAccountMembersDTO,
  guests: TrustAccountGuestDTO[],
  t: TFunction
): string {
  return t('share.trigger.status', {
    firm: t('share.trigger.firm', {count: getRosterPeople(roster).length}),
    guests: guests.length
      ? t('share.trigger.guestCount', {count: guests.length})
      : t('share.trigger.noGuests')
  });
}
